/**
 * watcher-feed.ts — #watcher channel renderer (channel_kind='watcher_log').
 *
 * GitHub-Actions-runs-style timeline of watcher cycle cards. Each watcher
 * cycle post is parsed from its pipe-separated body into a structured card
 * with status pill, compact metadata grid, and "Show raw" expander.
 *
 * Polls /api/messages?channel=watcher every 10s. Does NOT add a new API
 * endpoint — reuses the existing messages query.
 *
 * Mitchell ask 2026-04-27: "can you make watcher output a css or something?"
 */

import { get } from '../api';
import { state } from '../state';

// ── Types ──────────────────────────────────────────────────────────────

type CycleStatus = 'clean' | 'active' | 'warn' | 'escalate';

interface WatcherCycle {
  kind: 'cycle';
  messageId: string;
  timestamp: string;
  cycleNumber: number;
  health: string;
  devsActive: string;
  branches: string;
  deploy: string;
  db: string;
  stalls: string;
  dispatch: string;
  stepIns: string;
  watcherPost: string;
  snapshot: string;
  status: CycleStatus;
  raw: string;
  createdAt: string;
}

interface StepInEvent {
  kind: 'step_in';
  messageId: string;
  timestamp: string;
  content: string;
  raw: string;
  createdAt: string;
}

interface EscalateEvent {
  kind: 'escalate';
  messageId: string;
  timestamp: string;
  content: string;
  raw: string;
  createdAt: string;
}

interface UnparseableEvent {
  kind: 'unparseable';
  messageId: string;
  raw: string;
  error: string;
  createdAt: string;
}

type WatcherEvent = WatcherCycle | StepInEvent | EscalateEvent | UnparseableEvent;

interface ApiMessage {
  id: string;
  server_id: string;
  channel: string;
  author_id: string;
  content: string;
  created_at: string;
}

// ── Constants ──────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 10_000;
const MAX_CARDS = 200;

// ── Module state ───────────────────────────────────────────────────────

let pollTimer: ReturnType<typeof setInterval> | null = null;
let containerEl: HTMLElement | null = null;
let activeFilter: 'all' | 'active' | 'warn' | 'escalate' = 'all';
let lastMessageId: string | null = null;

// ── Helpers ────────────────────────────────────────────────────────────

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function relativeTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const diffMs = Date.now() - t;
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

function absoluteTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// ── Parser ─────────────────────────────────────────────────────────────

function extractField(body: string, prefix: string): string {
  // Fields are pipe-separated: "| health: ... | devs-active: ..."
  // Also handle the first field which may not have a leading pipe.
  const regex = new RegExp(`(?:^|\\|)\\s*${prefix}:\\s*([^|]*)`, 'i');
  const m = body.match(regex);
  return m && m[1] ? m[1].trim() : '';
}

function extractTimestamp(body: string): string {
  const m = body.match(/^\[([^\]]+)\]/);
  return m && m[1] ? m[1] : '';
}

function extractCycleNumber(body: string): number {
  const m = body.match(/CYCLE\s+(\d+)/i);
  return m && m[1] ? parseInt(m[1], 10) : 0;
}

function classifyCycle(parsed: {
  stalls: string;
  stepIns: string;
  dispatch: string;
  branches: string;
  health: string;
  deploy: string;
  raw: string;
}): CycleStatus {
  const raw = parsed.raw;

  // Escalate: explicit ESCALATE flag, DB drop, or >=3 unhealthy
  if (/ESCALATE/i.test(raw)) return 'escalate';
  // Check for DB drop (negative delta)
  const dbMatch = parsed.raw.match(/msg=\d+\s*\(.*?([+-]\d+)/);
  if (dbMatch && dbMatch[1] && parseInt(dbMatch[1], 10) < -10) return 'escalate';
  // Check for >=3 unhealthy in health field
  const healthMatch = parsed.health.match(/(\d+)\/(\d+)/);
  if (healthMatch && healthMatch[1] && healthMatch[2]) {
    const up = parseInt(healthMatch[1], 10);
    const total = parseInt(healthMatch[2], 10);
    if (total - up >= 3) return 'escalate';
  }

  // Warn: stalled dev, dispatch guard tripped, fetch-failure trend
  const stallCount = parsed.stalls.match(/^(\d+)/);
  if (stallCount && stallCount[1] && parseInt(stallCount[1], 10) > 0) return 'warn';
  if (/guard\s+(tripped|blocked)/i.test(parsed.dispatch)) return 'warn';
  if (/fetch-fail/i.test(parsed.deploy) && /trend|multiple|(\d+)\s+fetch-fail/i.test(parsed.deploy)) return 'warn';
  // Stalled dev mentioned in devs-active
  const staleParts = raw.split('stale');
  if (/stale|stall/i.test(raw) && !/known/i.test((staleParts[1] ?? '').slice(0, 30))) return 'warn';

  // Active: had step-ins, merged branches, dispatched a dev
  const stepInCount = parsed.stepIns.match(/^(\d+)/);
  if (stepInCount && stepInCount[1] && parseInt(stepInCount[1], 10) > 0) return 'active';
  const branchMerged = parsed.branches.match(/(\d+)\s+unmerged/);
  if (branchMerged && branchMerged[1] && parseInt(branchMerged[1], 10) > 0) return 'active';
  if (/dispatched|dispatch.*\bdev\d/i.test(parsed.dispatch)) return 'active';

  return 'clean';
}

function parseMessage(msg: ApiMessage): WatcherEvent {
  const body = msg.content.trim();

  // Step-in events: separate posts starting with [STEP-IN]
  if (/^\[STEP-IN\]/i.test(body)) {
    return {
      kind: 'step_in',
      messageId: msg.id,
      timestamp: extractTimestamp(body) || msg.created_at,
      content: body.replace(/^\[STEP-IN\]\s*/i, ''),
      raw: body,
      createdAt: msg.created_at,
    };
  }

  // Escalation events: posts with ESCALATE
  if (/\bESCALATE\b/i.test(body) && !/CYCLE\s+\d+/i.test(body)) {
    return {
      kind: 'escalate',
      messageId: msg.id,
      timestamp: extractTimestamp(body) || msg.created_at,
      content: body,
      raw: body,
      createdAt: msg.created_at,
    };
  }

  // Cycle posts: contain "CYCLE N"
  if (/CYCLE\s+\d+/i.test(body)) {
    const parsed = {
      health: extractField(body, 'health'),
      devsActive: extractField(body, 'devs-active'),
      branches: extractField(body, 'branches'),
      deploy: extractField(body, 'deploy'),
      db: extractField(body, 'DB'),
      stalls: extractField(body, 'stalls'),
      dispatch: extractField(body, 'dispatch'),
      stepIns: extractField(body, 'step-ins'),
      watcherPost: extractField(body, '#watcher'),
      snapshot: extractField(body, 'snapshot'),
      raw: body,
    };

    return {
      kind: 'cycle',
      messageId: msg.id,
      timestamp: extractTimestamp(body) || msg.created_at,
      cycleNumber: extractCycleNumber(body),
      ...parsed,
      status: classifyCycle(parsed),
      createdAt: msg.created_at,
    };
  }

  // Unparseable: anything else
  return {
    kind: 'unparseable',
    messageId: msg.id,
    raw: body,
    error: 'Message does not match cycle, step-in, or escalate patterns',
    createdAt: msg.created_at,
  };
}

// ── Card rendering ─────────────────────────────────────────────────────

function statusLabel(status: CycleStatus): string {
  switch (status) {
    case 'clean': return 'Clean';
    case 'active': return 'Active';
    case 'warn': return 'Warning';
    case 'escalate': return 'Escalate';
  }
}

function metaGridRow(label: string, value: string): string {
  if (!value) return '';
  // Truncate long values for the grid
  const short = value.length > 60 ? value.slice(0, 57) + '...' : value;
  return `<div class="watcher-meta-cell">
    <span class="watcher-meta-label">${esc(label)}</span>
    <span class="watcher-meta-value" title="${esc(value)}">${esc(short)}</span>
  </div>`;
}

function cycleCardHtml(evt: WatcherCycle): string {
  const ts = evt.timestamp || evt.createdAt;
  const rel = relativeTime(ts);
  const abs = absoluteTime(ts);

  // Extract compact values for the metadata grid
  const healthBadge = evt.health.match(/(\d+\/\d+)/)?.[1] ?? evt.health;
  const devsCount = evt.devsActive.match(/^(\d+)/)?.[0] ?? '0';
  const stallCount = evt.stalls.match(/^(\d+)/)?.[0] ?? '0';
  const stepInCount = evt.stepIns.match(/^(\d+)/)?.[0] ?? '0';
  const branchInfo = evt.branches.match(/(\d+)\s+unmerged/)?.[0] ?? evt.branches.slice(0, 20);
  const dbInfo = evt.db.match(/msg=\d+\s*\([^)]*\)/)?.[0] ?? evt.db.slice(0, 30);
  // Extract mem/load from dispatch
  const memLoad = evt.dispatch.match(/mem=(\d+)MB\s+load=([\d.]+\/\d+)/);
  const memStr = memLoad ? `${memLoad[1]}MB` : '';
  const loadStr = memLoad ? memLoad[2] : '';

  return `<div class="watcher-card watcher-card-${evt.status}" data-status="${evt.status}" data-id="${esc(evt.messageId)}">
    <div class="watcher-card-pill"></div>
    <div class="watcher-card-content">
      <div class="watcher-card-header">
        <span class="watcher-card-title">CYCLE ${evt.cycleNumber}</span>
        <span class="watcher-card-badge watcher-badge-${evt.status}">${esc(statusLabel(evt.status))}</span>
        <span class="watcher-card-time" title="${esc(abs)}">${esc(rel)}</span>
      </div>
      <div class="watcher-meta-grid">
        ${metaGridRow('health', healthBadge)}
        ${metaGridRow('devs', devsCount + ' active')}
        ${metaGridRow('stalls', stallCount)}
        ${metaGridRow('step-ins', stepInCount)}
        ${metaGridRow('branches', branchInfo)}
        ${metaGridRow('DB', dbInfo)}
        ${memStr ? metaGridRow('mem', memStr) : ''}
        ${loadStr ? metaGridRow('load', loadStr) : ''}
      </div>
      <details class="watcher-raw-expander">
        <summary class="watcher-raw-toggle">Show raw</summary>
        <pre class="watcher-raw-body">${esc(evt.raw)}</pre>
      </details>
    </div>
  </div>`;
}

function stepInCardHtml(evt: StepInEvent): string {
  const ts = evt.timestamp || evt.createdAt;
  const rel = relativeTime(ts);
  const abs = absoluteTime(ts);

  return `<div class="watcher-card watcher-card-active" data-status="active" data-id="${esc(evt.messageId)}">
    <div class="watcher-card-pill"></div>
    <div class="watcher-card-content">
      <div class="watcher-card-header">
        <span class="watcher-card-title">STEP-IN</span>
        <span class="watcher-card-badge watcher-badge-active">Action</span>
        <span class="watcher-card-time" title="${esc(abs)}">${esc(rel)}</span>
      </div>
      <div class="watcher-card-body">${esc(evt.content.length > 200 ? evt.content.slice(0, 197) + '...' : evt.content)}</div>
      <details class="watcher-raw-expander">
        <summary class="watcher-raw-toggle">Show raw</summary>
        <pre class="watcher-raw-body">${esc(evt.raw)}</pre>
      </details>
    </div>
  </div>`;
}

function escalateCardHtml(evt: EscalateEvent): string {
  const ts = evt.timestamp || evt.createdAt;
  const rel = relativeTime(ts);
  const abs = absoluteTime(ts);

  return `<div class="watcher-card watcher-card-escalate" data-status="escalate" data-id="${esc(evt.messageId)}">
    <div class="watcher-card-pill"></div>
    <div class="watcher-card-content">
      <div class="watcher-card-header">
        <span class="watcher-card-title">ESCALATION</span>
        <span class="watcher-card-badge watcher-badge-escalate">Escalate</span>
        <span class="watcher-card-time" title="${esc(abs)}">${esc(rel)}</span>
      </div>
      <div class="watcher-card-body">${esc(evt.content.length > 200 ? evt.content.slice(0, 197) + '...' : evt.content)}</div>
      <details class="watcher-raw-expander">
        <summary class="watcher-raw-toggle">Show raw</summary>
        <pre class="watcher-raw-body">${esc(evt.raw)}</pre>
      </details>
    </div>
  </div>`;
}

function unparseableCardHtml(evt: UnparseableEvent): string {
  return `<div class="watcher-card watcher-card-unparseable" data-status="unparseable" data-id="${esc(evt.messageId)}">
    <div class="watcher-card-pill"></div>
    <div class="watcher-card-content">
      <div class="watcher-card-header">
        <span class="watcher-card-title">UNPARSEABLE</span>
        <span class="watcher-card-badge watcher-badge-unparseable">Unknown</span>
      </div>
      <div class="watcher-card-body watcher-card-error">${esc(evt.error)}</div>
      <details class="watcher-raw-expander">
        <summary class="watcher-raw-toggle">Show raw</summary>
        <pre class="watcher-raw-body">${esc(evt.raw)}</pre>
      </details>
    </div>
  </div>`;
}

function eventCardHtml(evt: WatcherEvent): string {
  switch (evt.kind) {
    case 'cycle': return cycleCardHtml(evt);
    case 'step_in': return stepInCardHtml(evt);
    case 'escalate': return escalateCardHtml(evt);
    case 'unparseable': return unparseableCardHtml(evt);
  }
}

function emptyHtml(): string {
  return `<div class="watcher-empty">
    <div class="watcher-empty-title">Watcher hasn't posted yet</div>
    <div class="watcher-empty-body">
      First cycle takes ~3 min after dispatch. Check
      <code>cc-memory/hub-watcher.log</code> on the watcher container.
    </div>
  </div>`;
}

// ── Filter bar ─────────────────────────────────────────────────────────

function filterBarHtml(): string {
  const filters: Array<{ key: typeof activeFilter; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'warn', label: 'Warn' },
    { key: 'escalate', label: 'Escalate' },
  ];
  return `<div class="watcher-filter-bar" id="watcher-filter-bar">
    ${filters.map(f => `<button class="watcher-filter-btn${activeFilter === f.key ? ' watcher-filter-active' : ''}" data-filter="${f.key}">${esc(f.label)}</button>`).join('')}
  </div>`;
}

function applyFilter(): void {
  if (!containerEl) return;
  const cards = containerEl.querySelectorAll<HTMLElement>('.watcher-card');
  cards.forEach(card => {
    const status = card.dataset['status'] ?? '';
    if (activeFilter === 'all') {
      card.style.display = '';
    } else {
      card.style.display = status === activeFilter ? '' : 'none';
    }
  });
  // Update active button
  const bar = containerEl.querySelector('#watcher-filter-bar');
  if (bar) {
    bar.querySelectorAll<HTMLElement>('.watcher-filter-btn').forEach(btn => {
      btn.classList.toggle('watcher-filter-active', btn.dataset['filter'] === activeFilter);
    });
  }
}

function wireFilterBar(): void {
  if (!containerEl) return;
  const bar = containerEl.querySelector('#watcher-filter-bar');
  if (!bar) return;
  bar.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('.watcher-filter-btn');
    if (!btn) return;
    const f = btn.dataset['filter'] as typeof activeFilter;
    if (f) {
      activeFilter = f;
      applyFilter();
    }
  });
}

// ── Fetch + render ─────────────────────────────────────────────────────

function renderEvents(events: WatcherEvent[]): void {
  if (!containerEl) return;

  if (events.length === 0) {
    containerEl.innerHTML = filterBarHtml() + emptyHtml();
    wireFilterBar();
    return;
  }

  // Reverse-chronological: newest first. Take max MAX_CARDS.
  const sorted = events.slice().sort((a, b) => {
    return b.createdAt.localeCompare(a.createdAt);
  });
  const capped = sorted.slice(0, MAX_CARDS);

  const cardsHtml = capped.map(eventCardHtml).join('');
  containerEl.innerHTML = `
    ${filterBarHtml()}
    <div class="watcher-timeline" id="watcher-timeline">${cardsHtml}</div>
  `;

  wireFilterBar();
  applyFilter();
}

async function fetchAndRender(): Promise<void> {
  const sid = state.currentServer;
  if (!sid) return;

  try {
    const msgs = await get<ApiMessage[]>(
      `/api/messages?server_id=${encodeURIComponent(sid)}&channel=watcher&limit=${MAX_CARDS}`,
    );
    if (!msgs) return;

    const events = msgs.map(parseMessage);
    // Track latest for fade-in detection
    if (msgs.length > 0) {
      const newest = msgs.reduce((a, b) => a.created_at > b.created_at ? a : b);
      const isNew = lastMessageId !== null && newest.id !== lastMessageId;
      lastMessageId = newest.id;

      renderEvents(events);

      // Fade-in the newest card if it arrived after initial load
      if (isNew) {
        const timeline = containerEl?.querySelector('#watcher-timeline');
        const firstCard = timeline?.firstElementChild as HTMLElement | null;
        if (firstCard) {
          firstCard.classList.add('watcher-card-enter');
          requestAnimationFrame(() => {
            firstCard.classList.add('watcher-card-enter-active');
          });
        }
      }
    } else {
      renderEvents([]);
    }
  } catch {
    // Silent — keep last state, retry next tick
  }
}

// ── Public API ─────────────────────────────────────────────────────────

export function renderWatcherFeed(el: HTMLElement): void {
  containerEl = el;
  activeFilter = 'all';
  lastMessageId = null;
  el.classList.add('watcher-feed-body');
  el.innerHTML = `
    ${filterBarHtml()}
    <div class="watcher-timeline">
      <div class="watcher-loading">Loading watcher cycles...</div>
    </div>
  `;
  wireFilterBar();

  void fetchAndRender();
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = setInterval(() => void fetchAndRender(), POLL_INTERVAL_MS);
}

export function cleanupWatcherFeed(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  containerEl = null;
  lastMessageId = null;
}
